/**
 * The scrolling message list.
 *
 * Auto-scroll is conditional on purpose. The old screen called
 * `scrollIntoView` on every message and loading change, so scrolling up to
 * re-read an earlier answer while a reply streamed yanked the view back to the
 * bottom on the next chunk. Here the view follows the stream only while the
 * user is already near the bottom, and a "jump to latest" button appears when
 * they are not.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useI18n } from '../../../i18n';
import AssistantMessage from './AssistantMessage';

/** How close to the bottom still counts as "following the conversation". */
const NEAR_BOTTOM_PX = 120;

const AssistantThread = ({
  messages,
  isStreaming,
  editingMessageId,
  onCopy,
  onRegenerate,
  onStartEdit,
  onCancelEdit,
  onSubmitEdit,
  onNavigate,
  runs,
  isHeld,
  onRunBuild,
  onCancelBuild,
  onRenameClasses,
  interview,
}) => {
  const { t } = useI18n();
  const scrollRef = useRef(null);
  const [pinned, setPinned] = useState(true);

  const isNearBottom = useCallback(() => {
    const node = scrollRef.current;
    if (!node) return true;
    return node.scrollHeight - node.scrollTop - node.clientHeight <= NEAR_BOTTOM_PX;
  }, []);

  const scrollToBottom = useCallback((behavior = 'smooth') => {
    const node = scrollRef.current;
    if (!node) return;
    node.scrollTo({ top: node.scrollHeight, behavior });
    setPinned(true);
  }, []);

  const onScroll = useCallback(() => setPinned(isNearBottom()), [isNearBottom]);

  // Follow new content only while pinned. `messages` identity changes on every
  // streamed chunk, which is exactly the cadence we want to react to.
  useEffect(() => {
    if (pinned) scrollToBottom(isStreaming ? 'auto' : 'smooth');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, isStreaming]);

  // A newly opened thread starts at the bottom without animating there.
  useEffect(() => {
    scrollToBottom('auto');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // `!m.kind` keeps a build card from becoming "the last assistant message" and
  // so acquiring the Regenerate button — re-asking a question cannot re-run an
  // import, and the card's own controls are the only way to do that.
  const lastAssistantId = [...messages]
    .reverse()
    .find((m) => m.role === 'assistant' && !m.kind)?.id;

  return (
    <div className="lf-thread">
      <div className="lf-thread__scroll" ref={scrollRef} onScroll={onScroll}>
        <div className="lf-thread__inner">
          {messages.map((message) => (
            <AssistantMessage
              key={message.id}
              message={message}
              isLast={message.id === lastAssistantId}
              isStreaming={isStreaming}
              editing={message.id === editingMessageId}
              onCopy={onCopy}
              onRegenerate={onRegenerate}
              onStartEdit={onStartEdit}
              onCancelEdit={onCancelEdit}
              onSubmitEdit={onSubmitEdit}
              onNavigate={onNavigate}
              run={(runs || {})[message.id]}
              isHeld={isHeld}
              onRunBuild={onRunBuild}
              onCancelBuild={onCancelBuild}
              onRenameClasses={onRenameClasses}
              interview={interview}
            />
          ))}
        </div>
      </div>

      {!pinned && (
        <button
          type="button"
          className="lf-thread__jump"
          onClick={() => scrollToBottom()}
          title={t('assistant.thread.jump')}
          aria-label={t('assistant.thread.jump')}
        >
          <i className="fa fa-arrow-down" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

export default AssistantThread;
